import { useDisclosure } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

interface UseSidePanelStateOptions {
  /** Desktop (lg+) — split view only exists above this breakpoint. */
  isLargeScreen: boolean;
}

/**
 * Side-panel (split view) state.
 *
 * Owns the disclosure, the rendered content, the unsaved-changes handoff on
 * content swap, and the two auto-close effects (navigation, small screen).
 *
 * It knows nothing about Bird. The split view and Bird still cannot share the
 * viewport, but that edge now lives in Layout, which vacates the left-panel slot
 * before opening the split view — there is no cycle left to bridge with a ref.
 */
export function useSidePanelState({ isLargeScreen }: UseSidePanelStateOptions) {
  const location = useLocation();

  // Side panel state (split view only)
  const {
    isOpen: isSidePanelOpen,
    onOpen: onOpenSidePanel,
    onClose: onCloseSidePanel,
  } = useDisclosure({
    defaultIsOpen: false,
  });

  // Side panel content state
  const [sidePanelContent, setSidePanelContent] = useState<ReactNode>(null);

  // Side panel context functions
  const openSidePanel = (content: ReactNode) => {
    // If panel is already open with content, check for unsaved changes first
    if (isSidePanelOpen && sidePanelContent) {
      const hasUnsavedChanges = (window as any).__noteViewHasUnsavedChanges;
      const handleClose = (window as any).__noteViewHandleClose;

      if (hasUnsavedChanges && handleClose) {
        // Store pending content to open after save/discard
        (window as any).__pendingSidePanelContent = content;
        (window as any).__pendingSidePanelAction = () => {
          setSidePanelContent(content);
          delete (window as any).__pendingSidePanelContent;
          delete (window as any).__pendingSidePanelAction;
        };

        // Trigger unsaved changes dialog
        handleClose();
      } else {
        // No unsaved changes, just swap the content without closing the panel
        setSidePanelContent(content);
      }
    } else {
      // Panel is closed, open normally
      setSidePanelContent(content);
      onOpenSidePanel();
    }
  };

  const handleCloseSidePanel = useCallback(() => {
    setSidePanelContent(null);
    onCloseSidePanel();
  }, [onCloseSidePanel]);

  // Close side panel on navigation
  const [prevPathname, setPrevPathname] = useState(location.pathname);

  useEffect(() => {
    if (location.pathname !== prevPathname) {
      if (isSidePanelOpen) {
        handleCloseSidePanel();
      }
      setPrevPathname(location.pathname);
    }
  }, [location.pathname, prevPathname, isSidePanelOpen, handleCloseSidePanel]);

  // Auto-close split view if screen becomes too small
  useEffect(() => {
    if (isSidePanelOpen && !isLargeScreen) {
      handleCloseSidePanel();
    }
  }, [isLargeScreen, isSidePanelOpen, handleCloseSidePanel]);

  return {
    isSidePanelOpen,
    sidePanelContent,
    /**
     * Opens the split view WITHOUT the Bird carve-out — Layout wraps this to
     * vacate the left-panel slot first (see its comment). Callers should use the
     * SidePanelContext's `onOpen`, which is the wrapped one.
     */
    openSidePanel,
    handleCloseSidePanel,
    /**
     * Shared navigation tracker — Bird's own close-on-navigation effect reads
     * the SAME previous pathname (this effect is the only writer), so it stays
     * a single source of truth instead of two independently-updated copies.
     */
    prevPathname,
  };
}
